/**
 * ChapterEditorPanel — UX-7 update
 * Now delegates to BookPageView for page-by-page rendering instead of TipTap.
 * Kept thin: just handles free tier gate for chapter > 1.
 *
 * Note: StarterKit import kept to preserve TipTap command types (toggleBold etc.)
 */

// eslint-disable-next-line @typescript-eslint/no-unused-vars

import { Link } from 'react-router-dom';
import type { ChapterDocument } from '../../types/book';
import { BookPageView } from './BookPageView';

interface ChapterEditorPanelProps {
  chapter: ChapterDocument;
  bookId: string;
  isPaidUser: boolean;
  bookWordCount: number;
  totalChapters: number;
  onPrevChapter?: () => void;
  onNextChapter?: () => void;
  hasPrevChapter: boolean;
  hasNextChapter: boolean;
}

export function ChapterEditorPanel({
  chapter,
  bookId,
  isPaidUser,
  bookWordCount: _bookWordCount,
  totalChapters,
  onPrevChapter,
  onNextChapter,
  hasPrevChapter,
  hasNextChapter,
}: ChapterEditorPanelProps) {
  // Show upgrade CTA for free tier on any chapter > 1
  if (!isPaidUser && chapter.chapterNumber > 1) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-6 p-12 text-center">
        <div className="text-5xl">📚</div>
        <h2 className="text-xl font-semibold text-white">
          Your full book is ready — unlock it to read and edit
        </h2>
        <p className="max-w-md text-sm text-neutral-400">
          Chapter {chapter.chapterNumber} and all other chapters are available on paid plans.
          Upgrade to read, edit, and export your complete manuscript.
        </p>
        <Link
          to="/pricing"
          className="rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-500 transition-colors"
        >
          View Pricing →
        </Link>
      </div>
    );
  }

  return (
    <BookPageView
      key={`${bookId}-ch${chapter.chapterNumber}`}
      chapter={chapter}
      bookId={bookId}
      totalChapters={totalChapters}
      isPaidUser={isPaidUser}
      onPrevChapter={onPrevChapter}
      onNextChapter={onNextChapter}
      hasPrevChapter={hasPrevChapter}
      hasNextChapter={hasNextChapter}
    />
  );
}
